// Currency utilities for global rental marketplace
// Original price is source of truth - conversions are for display only

// Exchange rates relative to USD (approximate, for display purposes only)
// In production, these would come from a real-time API
const EXCHANGE_RATES = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  AUD: 1.53,
  CAD: 1.36,
  INR: 83.12,
  CNY: 7.24,
  NPR: 133.50,
  THB: 35.80,
  SGD: 1.34,
  MYR: 4.72,
  PHP: 56.20,
  IDR: 15650,
  VND: 24500,
  KRW: 1320,
  BRL: 4.97,
  MXN: 17.15,
  ZAR: 18.90,
  AED: 3.67,
  SAR: 3.75,
  CHF: 0.88,
  NZD: 1.64,
  HKD: 7.82,
  TWD: 31.50,
  SEK: 10.45,
  NOK: 10.65,
  DKK: 6.88,
  PLN: 4.02,
  CZK: 22.80,
  HUF: 358,
  TRY: 32.50,
  RUB: 92.50,
  PKR: 278,
  BDT: 110,
  LKR: 325,
  EGP: 30.90,
  NGN: 1550,
  KES: 153,
  GHS: 12.50,
  UGX: 3780,
  TZS: 2520,
};

// Currency symbols and formatting info
const CURRENCY_INFO = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB' },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA' },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN' },
  NPR: { symbol: 'रू', name: 'Nepalese Rupee', locale: 'ne-NP' },
  THB: { symbol: '฿', name: 'Thai Baht', locale: 'th-TH' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', locale: 'ms-MY' },
  PHP: { symbol: '₱', name: 'Philippine Peso', locale: 'en-PH' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', locale: 'vi-VN' },
  KRW: { symbol: '₩', name: 'South Korean Won', locale: 'ko-KR' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
  MXN: { symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX' },
  ZAR: { symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' },
  SAR: { symbol: 'ر.س', name: 'Saudi Riyal', locale: 'ar-SA' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ' },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', locale: 'zh-HK' },
  TWD: { symbol: 'NT$', name: 'Taiwan Dollar', locale: 'zh-TW' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', locale: 'sv-SE' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', locale: 'nb-NO' },
  DKK: { symbol: 'kr', name: 'Danish Krone', locale: 'da-DK' },
  PLN: { symbol: 'zł', name: 'Polish Zloty', locale: 'pl-PL' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna', locale: 'cs-CZ' },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint', locale: 'hu-HU' },
  TRY: { symbol: '₺', name: 'Turkish Lira', locale: 'tr-TR' },
  RUB: { symbol: '₽', name: 'Russian Ruble', locale: 'ru-RU' },
  PKR: { symbol: '₨', name: 'Pakistani Rupee', locale: 'ur-PK' },
  BDT: { symbol: '৳', name: 'Bangladeshi Taka', locale: 'bn-BD' },
  LKR: { symbol: 'රු', name: 'Sri Lankan Rupee', locale: 'si-LK' },
  EGP: { symbol: 'E£', name: 'Egyptian Pound', locale: 'ar-EG' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', locale: 'sw-KE' },
  GHS: { symbol: 'GH₵', name: 'Ghanaian Cedi', locale: 'en-GH' },
  UGX: { symbol: 'USh', name: 'Ugandan Shilling', locale: 'en-UG' },
  TZS: { symbol: 'TSh', name: 'Tanzanian Shilling', locale: 'sw-TZ' },
};

// Country to currency mapping
const COUNTRY_CURRENCY = {
  US: 'USD', USA: 'USD',
  GB: 'GBP', UK: 'GBP',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  JP: 'JPY',
  AU: 'AUD',
  CA: 'CAD',
  IN: 'INR',
  CN: 'CNY',
  NP: 'NPR',
  TH: 'THB',
  SG: 'SGD',
  MY: 'MYR',
  PH: 'PHP',
  ID: 'IDR',
  VN: 'VND',
  KR: 'KRW',
  BR: 'BRL',
  MX: 'MXN',
  ZA: 'ZAR',
  AE: 'AED',
  SA: 'SAR',
  CH: 'CHF',
  NZ: 'NZD',
  HK: 'HKD',
  TW: 'TWD',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  TR: 'TRY',
  RU: 'RUB',
  PK: 'PKR',
  BD: 'BDT',
  LK: 'LKR',
  EG: 'EGP',
  NG: 'NGN',
  KE: 'KES',
  GH: 'GHS',
  UG: 'UGX',
  TZ: 'TZS',
};

// Phone country code to currency mapping
const PHONE_CODE_CURRENCY = {
  '1': 'USD',     // USA, Canada
  '44': 'GBP',    // UK
  '81': 'JPY',    // Japan
  '86': 'CNY',    // China
  '91': 'INR',    // India
  '977': 'NPR',   // Nepal
  '66': 'THB',    // Thailand
  '65': 'SGD',    // Singapore
  '60': 'MYR',    // Malaysia
  '63': 'PHP',    // Philippines
  '62': 'IDR',    // Indonesia
  '84': 'VND',    // Vietnam
  '82': 'KRW',    // South Korea
  '55': 'BRL',    // Brazil
  '52': 'MXN',    // Mexico
  '27': 'ZAR',    // South Africa
  '971': 'AED',   // UAE
  '966': 'SAR',   // Saudi Arabia
  '41': 'CHF',    // Switzerland
  '64': 'NZD',    // New Zealand
  '852': 'HKD',   // Hong Kong
  '886': 'TWD',   // Taiwan
  '46': 'SEK',    // Sweden
  '47': 'NOK',    // Norway
  '45': 'DKK',    // Denmark
  '48': 'PLN',    // Poland
  '420': 'CZK',   // Czech Republic
  '36': 'HUF',    // Hungary
  '90': 'TRY',    // Turkey
  '7': 'RUB',     // Russia
  '92': 'PKR',    // Pakistan
  '880': 'BDT',   // Bangladesh
  '94': 'LKR',    // Sri Lanka
  '20': 'EGP',    // Egypt
  '234': 'NGN',   // Nigeria
  '254': 'KES',   // Kenya
  '233': 'GHS',   // Ghana
  '256': 'UGX',   // Uganda
  '255': 'TZS',   // Tanzania
  '33': 'EUR',    // France
  '49': 'EUR',    // Germany
  '39': 'EUR',    // Italy
  '34': 'EUR',    // Spain
  '31': 'EUR',    // Netherlands
  '32': 'EUR',    // Belgium
  '43': 'EUR',    // Austria
  '351': 'EUR',   // Portugal
  '353': 'EUR',   // Ireland
  '358': 'EUR',   // Finland
  '30': 'EUR',    // Greece
  '61': 'AUD',    // Australia
};

// Billing period labels
export const BILLING_PERIODS = {
  daily: { label: 'day', shortLabel: '/day' },
  weekly: { label: 'week', shortLabel: '/week' },
  monthly: { label: 'month', shortLabel: '/mo' },
};

/**
 * Format a price in its original currency (source of truth)
 */
export function formatOriginalPrice(amount, currency, billingPeriod = 'monthly') {
  const info = CURRENCY_INFO[currency] || CURRENCY_INFO.USD;
  const period = BILLING_PERIODS[billingPeriod] || BILLING_PERIODS.monthly;
  
  try {
    const formatted = new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: amount >= 1000 ? 0 : 2,
    }).format(amount);
    
    return `${formatted}${period.shortLabel}`;
  } catch (e) {
    // Fallback formatting
    const symbol = info.symbol || currency;
    return `${symbol}${amount.toLocaleString()}${period.shortLabel}`;
  }
}

/**
 * Format price with original + approximate conversion
 */
export function formatPriceWithConversion(amount, originalCurrency, viewerCurrency, billingPeriod = 'monthly') {
  const original = formatOriginalPrice(amount, originalCurrency, billingPeriod);
  
  // If same currency, no conversion needed
  if (originalCurrency === viewerCurrency) {
    return { original, converted: null, showConversion: false };
  }
  
  // Calculate approximate conversion
  const originalRate = EXCHANGE_RATES[originalCurrency] || 1;
  const viewerRate = EXCHANGE_RATES[viewerCurrency] || 1;
  const convertedAmount = (amount / originalRate) * viewerRate;
  
  const info = CURRENCY_INFO[viewerCurrency] || CURRENCY_INFO.USD;
  const period = BILLING_PERIODS[billingPeriod] || BILLING_PERIODS.monthly;
  
  try {
    const formatted = new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: viewerCurrency,
      maximumFractionDigits: convertedAmount >= 1000 ? 0 : 0,
    }).format(Math.round(convertedAmount));
    
    return {
      original,
      converted: `≈ ${formatted}`,
      showConversion: true,
    };
  } catch (e) {
    const symbol = info.symbol || viewerCurrency;
    return {
      original,
      converted: `≈ ${symbol}${Math.round(convertedAmount).toLocaleString()}`,
      showConversion: true,
    };
  }
}

/**
 * Format price for map marker (compact)
 */
export function formatMarkerPrice(amount, currency) {
  const info = CURRENCY_INFO[currency] || CURRENCY_INFO.USD;
  
  if (amount >= 1000000) {
    return `${info.symbol}${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${info.symbol}${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  }
  return `${info.symbol}${amount}`;
}

/**
 * Detect preferred currency from various sources
 */
export function detectPreferredCurrency(options = {}) {
  const { userProfile, phoneCountryCode, browserLocale } = options;
  
  // 1. User profile setting (highest priority)
  if (userProfile?.preferred_currency && CURRENCY_INFO[userProfile.preferred_currency]) {
    return userProfile.preferred_currency;
  }
  
  // 2. Phone country code
  if (phoneCountryCode && PHONE_CODE_CURRENCY[phoneCountryCode]) {
    return PHONE_CODE_CURRENCY[phoneCountryCode];
  }
  
  // 3. Browser locale/region
  if (browserLocale) {
    const parts = browserLocale.split('-');
    const region = parts[1]?.toUpperCase() || parts[0]?.toUpperCase();
    if (region && COUNTRY_CURRENCY[region]) {
      return COUNTRY_CURRENCY[region];
    }
  }
  
  // 4. Try navigator.language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const parts = navigator.language.split('-');
    const region = parts[1]?.toUpperCase();
    if (region && COUNTRY_CURRENCY[region]) {
      return COUNTRY_CURRENCY[region];
    }
  }
  
  // 5. Default fallback
  return 'USD';
}

/**
 * Get list of all supported currencies for dropdown
 */
export function getSupportedCurrencies() {
  return Object.entries(CURRENCY_INFO).map(([code, info]) => ({
    code,
    symbol: info.symbol,
    name: info.name,
    label: `${code} - ${info.name}`,
  }));
}

/**
 * Get currency info
 */
export function getCurrencyInfo(currency) {
  return CURRENCY_INFO[currency] || CURRENCY_INFO.USD;
}

export { CURRENCY_INFO, EXCHANGE_RATES, COUNTRY_CURRENCY, PHONE_CODE_CURRENCY };
